import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Flame, Database, SignOut, ShieldStar, ChartBar, BookmarkSimple, List, X, UsersThree, GitDiff, ChartLineUp } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/");
  };

  const items = [
    { to: "/", label: "Advisor", Icon: Flame, testid: "nav-advisor" },
    { to: "/compare", label: "Compare", Icon: ChartBar, testid: "nav-compare" },
    ...(user?.is_admin || user?.database_access ? [
      { to: "/database", label: "Database", Icon: Database, testid: "nav-database" },
    ] : []),
    { to: "/saved", label: "Saved", Icon: BookmarkSimple, testid: "nav-saved" },
    { to: "/comparisons", label: "My Comparisons", Icon: GitDiff, testid: "nav-comparisons" },
    { to: "/usage", label: "Usage", Icon: ChartLineUp, testid: "nav-usage" },
    ...(user?.is_admin ? [
      { to: "/admin", label: "Admin", Icon: ShieldStar, testid: "nav-admin" },
      { to: "/admin/access", label: "Access", Icon: UsersThree, testid: "nav-access" },
    ] : []),
  ];

  const NavLink = ({ to, label, Icon, testid, mobile }) => {
    const active = location.pathname === to;
    return (
      <Link
        to={to}
        data-testid={mobile ? `${testid}-mobile` : testid}
        onClick={() => setMenuOpen(false)}
        className={
          mobile
            ? `flex items-center gap-3 px-4 py-3 text-sm font-mono uppercase tracking-wide border-l-2 transition-colors ${active ? "border-primary text-white bg-muted/40" : "border-transparent text-muted-foreground hover:text-white"}`
            : `flex items-center gap-2 px-3 py-2 text-sm font-mono uppercase tracking-wide border-b-2 transition-colors ${active ? "border-primary text-white" : "border-transparent text-muted-foreground hover:text-white"}`
        }
      >
        <Icon size={16} weight="bold" />
        {label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur-md">
      <div className="mx-auto max-w-[1400px] flex items-center justify-between px-4 sm:px-6 h-14">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5" onClick={() => setMenuOpen(false)}>
          <img src="/logo.png" alt="Thermal Spray Atlas" className="h-8 w-8 rounded-sm object-cover" />
          <div className="leading-none">
            <div className="font-heading font-black text-white text-sm tracking-tight">THERMAL SPRAY ATLAS</div>
            <div className="font-mono text-[10px] text-muted-foreground tracking-widest">AI COATING ADVISOR</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {items.map((it) => <NavLink key={it.to} {...it} />)}
          {user && (
            <div className="ml-2 flex items-center gap-2 pl-3 border-l border-border">
              {!user.is_admin && (
                <span data-testid="search-usage" className="font-mono text-[10px] text-muted-foreground hidden lg:inline">
                  {user.search_limit === -1 ? `${user.search_count || 0} / ∞` : `${user.search_count || 0} / ${user.search_limit || 0}`} searches
                </span>
              )}
              {user.picture && (
                <img src={user.picture} alt="" className="h-7 w-7 rounded-sm object-cover" referrerPolicy="no-referrer" />
              )}
              <Button data-testid="logout-btn" onClick={handleLogout} variant="ghost" size="sm" className="rounded-sm text-muted-foreground hover:text-primary">
                <SignOut size={16} weight="bold" />
              </Button>
            </div>
          )}
        </nav>

        {/* Mobile hamburger */}
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMenuOpen((o) => !o)}
          className="md:hidden flex h-9 w-9 items-center justify-center rounded-sm border border-border text-white hover:border-primary transition-colors"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
        </button>
      </div>

      {/* Mobile menu panel */}
      {menuOpen && (
        <nav data-testid="mobile-menu" className="md:hidden border-t border-border bg-background">
          {items.map((it) => <NavLink key={it.to} {...it} mobile />)}
          {user && (
            <button
              data-testid="logout-btn-mobile"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-mono uppercase tracking-wide border-l-2 border-transparent text-muted-foreground hover:text-primary"
            >
              <SignOut size={16} weight="bold" /> Sign Out
              <span className="ml-auto font-mono text-[10px] normal-case text-muted-foreground truncate max-w-[140px]">{user.email}</span>
            </button>
          )}
        </nav>
      )}
    </header>
  );
};
